import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import * as lottoApi from "@/lotto/api";
import * as userApi from "@/user/api";
import * as aptApi from "@/apt/api";
import * as familyEventApi from "@/familyEvent/api";

export const { VUE_APP_TERRA_URL: TERRA_URL } = process.env;

export const user = () => {
  return userApi;
};

export const lotto = () => {
  return lottoApi;
};

export const apt = () => {
  return aptApi;
};

export const familyEvent = () => {
  return familyEventApi;
};

/**
 *
 * @param url
 * @param payload
 * @param headerOption
 * @returns
 */
export const get = async (
  url: string,
  payload?: any,
  headerOption: HeaderOption = new HeaderOption(),
  errorAlert = true,
  timeoutMs?: number
): Promise<AxiosResponse<any>> => {
  const config = getAxiosRequestConfig(
    TERRA_URL + url + getQuery(payload),
    "GET",
    {},
    await getHeaders(headerOption),
    timeoutMs
  );
  return await axiosRequest(config, errorAlert);
};

/**
 *
 * @param url
 * @param data
 * @param headerOption
 * @returns
 */
export async function post(
  url: string,
  data?: any,
  headerOption: HeaderOption = new HeaderOption(),
  timeoutMs?: number
): Promise<AxiosResponse<any>> {
  const config = getAxiosRequestConfig(
    TERRA_URL + url,
    "POST",
    data,
    await getHeaders(headerOption),
    timeoutMs
  );
  return await axiosRequest(config);
}

/**
 *
 * @param url
 * @param data
 * @returns
 */
export async function put(
  url: string,
  data?: any
): Promise<AxiosResponse<any>> {
  const config = getAxiosRequestConfig(
    TERRA_URL + url,
    "PUT",
    data,
    await getHeaders()
  );
  return await axiosRequest(config);
}

/**
 * 일부 필드만 바꿀 때 쓴다 (관리 화면의 on/off 등).
 * @param url
 * @param data 바꿀 필드만 담는다. 빠진 필드는 서버가 그대로 둔다
 */
export async function patch(
  url: string,
  data?: any
): Promise<AxiosResponse<any>> {
  const config = getAxiosRequestConfig(
    TERRA_URL + url,
    "PATCH",
    data,
    await getHeaders()
  );
  return await axiosRequest(config);
}

/**
 *
 * @param url
 * @param data
 * @returns
 */
export async function remove(
  url: string,
  data?: any
): Promise<AxiosResponse<any>> {
  const config = getAxiosRequestConfig(
    TERRA_URL + url,
    "DELETE",
    data,
    await getHeaders()
  );
  return await axiosRequest(config);
}

export class HeaderOption {
  contentType = "";
  responseType = "";
}

export const getHeaders = (option: HeaderOption = new HeaderOption()) => {
  const headers = {
    "Content-Type": "application/json",
    responseType: "",
  };

  if (option.contentType != "") {
    headers["Content-Type"] = option.contentType;
  }
  if (option.responseType != "") {
    headers.responseType = option.contentType;
  }
  return headers;
};

export const getQuery = (payload: any) => {
  let returnValue = "?";

  for (const a in payload) {
    returnValue += a;
    returnValue += "=";
    returnValue += encodeURIComponent(payload[a]);
    returnValue += "&";
  }

  return returnValue;
};

/**
 *
 * @param url url
 * @param method method
 * @param data body
 * @param headers header
 * @returns
 */
export const getAxiosRequestConfig = (
  url: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  data?: any,
  headers?: any,
  // 기본 타임아웃(10초)보다 오래 걸리는 API용. 미지정 시 인스턴스 기본값을 쓴다.
  timeoutMs?: number
) => {
  const config: AxiosRequestConfig = {
    method: method,
    url: url,
    data: data,
    headers: headers,
  };
  if (timeoutMs && timeoutMs > 0) {
    config.timeout = timeoutMs;
  }
  return config;
};

const preRequest = (config: AxiosRequestConfig) => {
  const logNumber = Math.floor(Math.random() * (999999 - 100000) + 100000);
  // 주의: HEADER/REQUEST BODY는 로그로 남기지 않는다 (로그인 등 요청 바디에 평문 비밀번호 포함).
  console.debug("[" + logNumber + "] API " + config.method + " " + config.url);
  return logNumber;
};

// Create a function to generate a dummy AxiosResponse for error cases
const createErrorResponse = (message: string): AxiosResponse<any> => {
  return {
    data: { message },
    status: 500,
    statusText: "Error",
    headers: {},
    config: {
      headers: {} as any,
    },
    request: {} as any,
  };
};

export const axiosRequest = async (
  config: AxiosRequestConfig,
  errorAlert = true
): Promise<AxiosResponse<any>> => {
  const logNumber = preRequest(config);
  console.log("errorAlert : ", errorAlert);
  try {
    const response: AxiosResponse<any> = await apiClient(config);
    console.log("[" + logNumber + "] API 통신 성공 ", response);
    return response.data;
  } catch (error: any) {
    console.log("[" + logNumber + "] [axios예외] API 요청 예외:", error);
    if (axios.isAxiosError(error)) {
      // Error is an Axios error
      const { response } = error;
      if (response) {
        const { status, data } = response;
        console.log(`[${logNumber}] errorResponse:`, data.message);
        let result = null;
        data.data = {};
        data.data.result = false;
        // Optionally handle different status codes
        switch (status) {
          case 400:
            // Handle 400 Bad Request
            break;
          case 401:
            // Handle 401 Unauthorized
            break;
          case 404:
            data.data.result = false;
            break;
          case 422:
            // 요청 유효성
            data.data.result = false;
            alert(getValidError(data.validErrors));
            result = data;
            break;
          case 500:
            // Handle 500 Internal Server Error

            if (errorAlert) {
              alert(data.message);
            }
            break;
          default:
            // Handle other status codes
            result = data;
            break;
        }

        // Return error message or a default message
        return data;
      } else {
        // Handle cases where response is undefined
        console.warn(`[${logNumber}] Network error or server is unreachable`);
        return createErrorResponse("Network error or server is unreachable");
      }
    } else {
      // Handle unexpected errors
      console.error(`[${logNumber}] Unexpected error:`, error);
      return createErrorResponse("Unexpected error occurred");
    }
  } finally {
    console.groupEnd();
  }
};

const apiClient: AxiosInstance = axios.create({
  baseURL: "https://your-api-base-url.com", // 기본 URL 설정
  timeout: 10000, // 요청 타임아웃 설정
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // 세션 쿠키가 자동으로 포함되도록 설정
});

const getValidError = (errors: any) => {
  const validMessage = errors.map((error: { field: any; message: any }) => {
    return `${error.message} \n`;
  });
  return validMessage;
};

const getFieldName = (field: string) => {
  switch (field) {
    case "currentPassword":
      return "비밀번호";
      break;
    default:
      return field;
  }
};
